import { memo, useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { Pen, AlertTriangle, Copy, Check, ChevronDown, ChevronRight, Brain } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import 'katex/dist/katex.min.css'
import MessageAvatar from './MessageAvatar'
import { Message } from '@/service/api'
import AnwserActions from './AnwserActions'
import { useAppStore } from '@/store'
import TooltipOverlay from '@/components/TooltipOverlay'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { convertMathNotation } from '@/utils/katexMathPreprocessor'
// RE-ENABLED: Backend Pydantic validation not working consistently (Error 424 failures)
// Keep frontend preprocessing as safety net until backend validation is stable
import { highlightAgentMentions } from '@/utils/highlightAgentMentions'
import TimeseriesCharts from './TimeseriesCharts'
import ReasoningPath from './ReasoningPath'
import { StructuredResponse } from './StructuredResponse'
import { SourcesList } from './SourcesList'
import { EngineeringResponse } from '@/service/api'
import {
  mergeMarkdownComponents,
  remarkPlugins as remarkPluginsConfig,
  rehypePlugins as rehypePluginsConfig,
} from '@/config/markdownConfig'
import type { PluggableList } from 'unified'
// import { normalizeAgentName, getDisplayName, AgentId } from '@/constants/agentNames' // Commented out with agent badge
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useUIv2 } from '@/hooks/useUIv2'
import { useTranslation } from '@/i18n'
import { RenderedAssistantMessage } from '@/features/chatV2/render'

import type { VersionInfo } from '@/features/chatV2/types'
import {
  ToolProgressStatus,
  type ToolProgressData,
} from '@/features/chatV2/components/messages/ToolProgressStatus'

type MessageBubbleProps = {
  message: Message
  handleFeedbackSubmitted: (messageId: string, rating: number | undefined) => void
  onEditClick?: (message: Message) => void
  isEditDisabled?: boolean
  // Inline editing props
  isEditing?: boolean
  isEditSaving?: boolean
  messagesAfterCount?: number
  onEditConfirm?: (newContent: string) => void
  onEditCancel?: () => void
  onRegenerate?: (assistantMessageId: string) => void
  isRegenerateDisabled?: boolean
  // Version navigation props
  versionInfo?: VersionInfo | null
  onNavigateVersion?: (userMsgId: string, direction: 'prev' | 'next') => void
  /** The preceding user question text for Q&A context in rating emails */
  userQuestion?: string
  /**
   * W16 / TIME-198: ephemeral tool-progress status. Set by `Messages`
   * only on the actively-streaming assistant bubble; null on every
   * other bubble. Renders directly under the Thinking-process pane so
   * the line is visually anchored to the turn it describes.
   */
  toolProgress?: ToolProgressData | null
}

// `markdownConfig` exports these as untyped arrays, so TS infers a widened
// element union instead of `PluggableList`. The values genuinely conform to
// `PluggableList` ([plugin, options] tuples are valid `Pluggable`s); assert the
// precise type once here so the three ReactMarkdown call sites typecheck.
const remarkPlugins = remarkPluginsConfig as PluggableList
const rehypePlugins = rehypePluginsConfig as PluggableList

const USER_ROLE = 'user'
const TIME_FORMAT = { hour: '2-digit', minute: '2-digit' } as const
const EDIT_MESSAGE = 'Edit message'

// Helper to extract structured response JSON from message content
const extractStructuredResponse = (content: string): EngineeringResponse | null => {
  if (typeof content !== 'string' || content.length === 0) return null
  const match = content.match(/```json:structured_response\n([\s\S]*?)\n```/)
  if (match) {
    try {
      return JSON.parse(match[1]) as EngineeringResponse
    } catch (e) {
      console.warn('Failed to parse structured response JSON:', e)
      return null
    }
  }
  return null
}

/** Thinking toggle – matches Figma ReasoningTrigger: logo + shimmer text + chevron */
function ThinkingToggle({ thinking }: { thinking: string }) {
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()
  return (
    <div className="mb-2">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-sm hover:opacity-80 transition-opacity"
      >
        <img
          src="/images/intuai-logo-icon.svg"
          alt=""
          className="w-4 h-4"
          aria-hidden="true"
        />
        <span className="text-sm font-medium text-orange-600 dark:text-orange-400">
          {t('chat:messages.thinking', { defaultValue: 'Thinking' })}
        </span>
        {open ? (
          <ChevronDown size={16} className="text-gray-400 dark:text-gray-500" />
        ) : (
          <ChevronRight size={16} className="text-gray-400 dark:text-gray-500" />
        )}
      </button>
      {open && (
        <div className="mt-2 p-3 bg-gray-50 dark:bg-dark-muted rounded-lg text-sm text-gray-600 dark:text-gray-400 italic">
          <ReactMarkdown
            components={mergeMarkdownComponents()}
            remarkPlugins={remarkPlugins}
            rehypePlugins={rehypePlugins}
          >
            {thinking}
          </ReactMarkdown>
        </div>
      )}
    </div>
  )
}

const MessageBubble = ({
  message,
  handleFeedbackSubmitted,
  onEditClick,
  isEditDisabled = false,
  isEditing = false,
  isEditSaving = false,
  messagesAfterCount = 0,
  onEditConfirm,
  onEditCancel,
  onRegenerate,
  isRegenerateDisabled = false,
  versionInfo,
  onNavigateVersion,
  userQuestion,
  toolProgress,
}: MessageBubbleProps) => {
  const { setInputValue } = useAppStore()
  const isV2 = useUIv2()
  const { t } = useTranslation()
  const { role, content, error, timestamp } = message
  const containerRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const isUser = role === USER_ROLE
  const date = new Date(timestamp).toLocaleTimeString([], TIME_FORMAT)

  // Inline editing state
  const [editedContent, setEditedContent] = useState(content)
  const [copied, setCopied] = useState(false)
  const copiedTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Auto-scroll is owned by Messages/index.tsx. Per-bubble scrollIntoView fought
  // the sticky-bottom scroll during streaming and made the sidebar flicker.

  // Reset edited content when entering edit mode or when message changes
  useEffect(() => {
    if (isEditing) {
      setEditedContent(content)
    }
  }, [isEditing, content])

  if (error) {
    return (
      <div className="rounded-lg px-4 py-3 bg-gray-100 dark:bg-dark-accent text-gray-600 dark:text-gray-400 text-sm">
        {t('chat:messages.no_answer', {
          defaultValue:
            "I wasn't able to find an answer for you. Please try again or rephrase your question.",
        })}
      </div>
    )
  }

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={`
        flex flex-col items-start ${
          isUser ? 'gap-1 justify-end items-end' : 'gap-1.5 justify-start items-start'
        }
      `}
    >
      {!isUser && (
        <div className="flex justify-between">
          <MessageAvatar isUser={isUser} agentUsed={message.agent_used} />
        </div>
      )}
      <div className="flex w-full items-center gap-3 justify-end">
        <motion.div
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className={cn(
            'rounded-lg p-3',
            isUser
              ? 'border bg-gray-100 dark:bg-dark-accent border-gray-200 dark:border-dark-border'
              : 'w-full bg-transparent'
          )}
        >
          {(() => {
            // AGNT-556: route prose-style reasoning (NestJS KG
            // path — Qwen chain-of-thought as a single flowing block)
            // to the Accordion display, and keep the structured
            // step-by-step ReasoningPath for Python's `{steps, ...}`
            // shape where the discrete steps actually mean something.
            // Detection: `reasoning_path.text` is set by the NestJS
            // writer and absent in Python's payload.
            const proseReasoning =
              typeof message.reasoning_path?.text === 'string'
                ? message.reasoning_path.text
                : null
            // FS-659: the two are no longer either/or. The native agent writes
            // ONE `reasoning_path` carrying both the openable `steps` and (when
            // reasoning tokens were on) the model's prose under `text`, so
            // suppressing the step list whenever prose exists hid every native
            // trail.
            //
            // AGNT-556's rule still holds for what it was actually about: the
            // kg-agent packs its whole chain-of-thought into `text` AND a
            // single synthetic step whose `description` IS that 5kB prose —
            // rendering it as "step 1: <essay>" is why prose routes to the
            // accordion. That shape carries no `detail`; a step built from a
            // real tool call always does. So `detail` is the discriminator.
            const hasOpenableSteps = (message.reasoning_path?.steps ?? []).some(
              (step) => !!step.detail
            )
            const showStructured =
              !!message.reasoning_path &&
              (message.reasoning_path.steps?.length ?? 0) > 0 &&
              (!proseReasoning || hasOpenableSteps)
            const accordionText = message.thinking ?? proseReasoning
            return (
              <>
                {showStructured && message.reasoning_path && (
                  <ReasoningPath reasoning_path={message.reasoning_path} />
                )}
                {/* Both may show. The step list is what the agent DID; this is
                    what it said to itself while doing it. Gating the prose on
                    `!showStructured` (as before) would silently drop the
                    Thinking pane from every native turn the moment that turn
                    also gained steps. */}
                {accordionText && (
                  isV2 ? (
                    <ThinkingToggle thinking={accordionText} />
                  ) : (
                    <div className="text-sm rounded-lg px-2 text-gray-500 dark:text-dark-text-muted bg-gray-50 dark:bg-dark-muted">
                      <Accordion type="single" collapsible className="w-full">
                        <AccordionItem value="thinking">
                          <AccordionTrigger className="p-1">
                            <span className="flex items-center gap-2">
                              <Brain size={16} className="mr-2" />
                              {t('chat:messages.thinking_process', { defaultValue: 'Thinking process' })}
                            </span>
                          </AccordionTrigger>
                          <AccordionContent>
                            <ReactMarkdown
                              components={mergeMarkdownComponents()}
                              remarkPlugins={remarkPlugins}
                              rehypePlugins={rehypePlugins}
                            >
                              {accordionText}
                            </ReactMarkdown>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    </div>
                  )
                )}
                {/* W16 / TIME-198: tool-progress status line — anchored
                  * directly under the Thinking pane on the streaming
                  * assistant bubble so the line is visually associated
                  * with the active turn. Only the active bubble
                  * receives `toolProgress`; other bubbles get null and
                  * skip this branch. */}
                {toolProgress && (
                  <div className="px-2 py-1">
                    <ToolProgressStatus data={toolProgress} />
                  </div>
                )}
                {!isUser && message.workflow_progress && (
                  <RenderedAssistantMessage parts={[message.workflow_progress]} />
                )}
                {/* FS-663: the agent's prepared-share confirm card. */}
                {!isUser && message.pending_share && (
                  <RenderedAssistantMessage parts={[message.pending_share]} />
                )}
              </>
            )
          })()}
          <div className={`text-md ${isUser ? '' : 'markdown-formatting'}`}>
            {isUser ? (
              isEditing ? (
                // INLINE EDIT MODE
                <div className="flex flex-col gap-3 w-full">
                  {messagesAfterCount > 0 && (
                    <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 text-sm bg-amber-50 dark:bg-amber-900/20 p-2 rounded-lg">
                      <AlertTriangle size={16} />
                      {t('chat:messages.edit_remove_warning', {
                        defaultValue:
                          'This will remove {{count}} message{{plural}} after this point.',
                        count: messagesAfterCount,
                        plural: messagesAfterCount > 1 ? 's' : '',
                      })}
                    </div>
                  )}
                  <Textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        if (editedContent.trim() && !isEditSaving) {
                          onEditConfirm?.(editedContent.trim())
                        }
                      }
                    }}
                    className="min-h-[80px] resize-none"
                    autoFocus
                    disabled={isEditSaving}
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={onEditCancel} disabled={isEditSaving}>
                      {t('chat:messages.cancel', { defaultValue: 'Cancel' })}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => onEditConfirm?.(editedContent.trim())}
                      disabled={!editedContent.trim() || isEditSaving}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      {isEditSaving
                        ? t('chat:messages.saving', { defaultValue: 'Saving...' })
                        : messagesAfterCount > 0
                          ? t('chat:messages.edit_and_continue', { defaultValue: 'Edit & Continue' })
                          : t('chat:messages.save_and_regenerate', { defaultValue: 'Save & Regenerate' })}
                    </Button>
                  </div>
                </div>
              ) : (
                // User messages: highlight @mentions but don't use markdown
                <div className="whitespace-pre-wrap">
                  {highlightAgentMentions(content)}
                </div>
              )
            ) : (
              // AI messages: check for structured response first, then fall back to markdown
              (() => {
                const structuredData = extractStructuredResponse(content)
                if (structuredData) {
                  return <StructuredResponse content={structuredData} />
                }
                // Fall back to markdown rendering
                return (
                  <ReactMarkdown
                    components={mergeMarkdownComponents()}
                    remarkPlugins={remarkPlugins}
                    rehypePlugins={rehypePlugins}
                  >
                    {convertMathNotation(content)}
                  </ReactMarkdown>
                )
              })()
            )}

            {/* Timeseries Charts - Rendered when chart data is present */}
            {!isUser && (message.charts || message.chart_data) && (
              <TimeseriesCharts
                charts={message.charts}
                chart_data={message.chart_data}
              />
            )}

            {/* ENG-2416 §10: frontier-style structured citations. Renders
                the `sources` array from either backend path as a single
                clickable Sources list, replacing the inconsistent
                inline-footer / nothing-at-all behavior. */}
            {!isUser && <SourcesList sources={message.sources} />}
          </div>
        </motion.div>
      </div>
      <div
        className={`w-full flex gap-2 items-center ps-3 ${
          isUser ? 'justify-end' : 'justify-start'
        }`}
      >
        {!isUser && (
          <AnwserActions
            message={message}
            handleFeedbackSubmitted={handleFeedbackSubmitted}
            onRegenerate={onRegenerate ? () => onRegenerate(message.id) : undefined}
            isRegenerateDisabled={isRegenerateDisabled}
            versionInfo={versionInfo}
            onNavigateVersion={onNavigateVersion}
            userQuestion={userQuestion}
          />
        )}
        {/* FS-612: the version pager is rendered once per turn — on the
            assistant bubble inside AnwserActions (next to the ↻ regenerate
            button), which is the answer being paged. The duplicate pager that
            used to render here on the user bubble produced two "go back to
            other tries" controls per turn. */}
        {/* DISABLED: Hide all agent badges from user view
        {!isUser && message.agent_used && (() => {
          const normalizedAgent = normalizeAgentName(message.agent_used)
          const agentColorClasses: Record<string, string> = {
            [AgentId.PLANT_SUPPORT]: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
            [AgentId.CHEMICAL]: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
            [AgentId.MECHANICAL]: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
            [AgentId.ELECTRICAL]: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
          }
          const colorClass = agentColorClasses[normalizedAgent] || 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
          return (
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-medium ${colorClass}`}
            >
              {getDisplayName(message.agent_used)}
            </span>
          )
        })()}
        */}
        <span className="text-sm opacity-60">
          {date}
          {!isUser && message.responseDurationMs != null && (() => {
            const rounded = Math.round(message.responseDurationMs)
            return (
              <>
                {' · '}
                {rounded < 1000
                  ? `${rounded}ms`
                  : `${(message.responseDurationMs / 1000).toFixed(1)}s`}
              </>
            )
          })()}
        </span>
        {isUser && !isEditing && (
          <>
            <TooltipOverlay message={t('chat:messages.copy_message', { defaultValue: 'Copy message' })}>
              <button
                className="p-1 opacity-60 hover:opacity-100 cursor-pointer transition-all rounded-full hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(content)
                    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current)
                    setCopied(true)
                    copiedTimerRef.current = setTimeout(() => setCopied(false), 2000)
                  } catch {
                    // Clipboard write failed silently
                  }
                }}
              >
                {copied ? (
                  <Check size={14} className="text-green-500" />
                ) : (
                  <Copy size={14} className="text-gray-500 dark:text-dark-text-muted" />
                )}
              </button>
            </TooltipOverlay>
            <TooltipOverlay message={t('chat:messages.edit_message', { defaultValue: EDIT_MESSAGE })}>
              <button
                className="p-1 opacity-60 hover:opacity-100 cursor-pointer transition-all rounded-full disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors"
                onClick={() => onEditClick ? onEditClick(message) : setInputValue(message.content)}
                disabled={isEditDisabled}
              >
                <Pen size={14} className="text-gray-500 dark:text-dark-text-muted" />
              </button>
            </TooltipOverlay>
          </>
        )}
      </div>
      <div ref={bottomRef}></div>
    </motion.div>
  )
}

export default memo(MessageBubble, (prev, next) => {
  if (prev.message !== next.message) return false
  if (prev.handleFeedbackSubmitted !== next.handleFeedbackSubmitted) return false
  if (prev.onEditClick !== next.onEditClick) return false
  if (prev.onRegenerate !== next.onRegenerate) return false
  if (prev.onNavigateVersion !== next.onNavigateVersion) return false
  if (prev.isEditDisabled !== next.isEditDisabled) return false
  if (prev.isEditing !== next.isEditing) return false
  if (prev.isEditSaving !== next.isEditSaving) return false
  if (prev.messagesAfterCount !== next.messagesAfterCount) return false
  if (prev.isRegenerateDisabled !== next.isRegenerateDisabled) return false
  if (prev.userQuestion !== next.userQuestion) return false
  if (prev.toolProgress !== next.toolProgress) return false
  if ((prev.isEditing || next.isEditing) && prev.onEditConfirm !== next.onEditConfirm) return false
  if ((prev.isEditing || next.isEditing) && prev.onEditCancel !== next.onEditCancel) return false

  const prevV = prev.versionInfo
  const nextV = next.versionInfo
  if ((prevV == null) !== (nextV == null)) return false
  if (prevV && nextV) {
    if (prevV.currentVersion !== nextV.currentVersion) return false
    if (prevV.totalVersions !== nextV.totalVersions) return false
    if (prevV.userMessageId !== nextV.userMessageId) return false
  }

  return true
})
